import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule, Provider } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppComponent } from './app.component';

// Angular Material Imports
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ConfigurationService } from './services/configuration.service';
import { MatDialogModule } from '@angular/material/dialog';
import { SourceDocumentDialogComponent } from './advisor/source-document-dialog/source-document-dialog.component';
import { SourceDocumentDialogNewComponent } from './advisor_new/source-document-dialog/source-document-dialog.component';
import { MessageListComponent } from './advisor_new/message-list/message-list.component';
import { AppRoutingModule } from './app-routing.module';
import { HomeComponent } from './home/home.component';
import { PromptLogComponent } from './prompt-log/prompt-log.component';
import { PlaygroundComponent } from './playground/playground.component';
import { AdvisorComponent } from './advisor/advisor.component';
import { AdvisorNewComponent } from './advisor_new/advisor-new.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { AlertModalComponent } from './alert-modal/alert-modal.component';
import { ConfirmationModalComponent } from './confirmation-modal/confirmation-modal.component';
import { AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import { AuthInterceptor } from './services/auth.interceptor';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { DraftSectionDialogComponent } from './draft-section-dialog/draft-section-dialog.component';
import { AgGridModule } from 'ag-grid-angular';
import { AdminComponent } from './admin/admin.component'; // Angular Grid Logic
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { UserModalComponent } from './admin/user-modal/user-modal.component';
import { ResearchDialogComponent } from './collections/research-dialog/research-dialog.component';
import { ViewResearchDialogComponent } from './collections/view-research-dialog/view-research-dialog.component';
import { EditCollectionDialogComponent } from './admin/edit-collection-dialog/edit-collection-dialog.component';
import { AddCollectionDialogComponent } from './admin/add-collection-dialog/add-collection-dialog.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ManageCollectionsComponent } from './admin/manage-collections/manage-collections.component';
import { ManageUsersComponent } from './admin/manage-users/manage-users.component';
import { ManagePersonasComponent } from './admin/manage-personas/manage-personas.component';
import { ManagePersonasViewComponent } from './admin/manage-personas/view/manage-personas-view.component';
import { ManageSystemPromptsComponent } from './admin/manage-system-prompts/manage-system-prompts.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { FilterModalComponent } from './advisor/filter-modal/filter-modal.component';
import { MockInterceptor } from './services/mock.interceptor';
import { environment } from 'src/environments/environment';
import { ManagePersonasEditComponent } from './admin/manage-personas/edit/manage-personas-edit.component';
import { UploadDialogComponent } from './admin/manage-personas/edit/upload-dialog/upload-dialog';
import { ReviewersComponent } from './reviewers/reviewers.component';
import { ReviewerInfoDialogComponent } from './reviewers/reviewer-info-dialog/reviewer-info-dialog';
import { ReviewersFilterDialogComponent } from './reviewers/filter-dialog/reviewers-filter-dialog.component';
import { MultiSelectReviewDialogComponent } from './reviewers/multi-select-review-dialog/multi-select-review-dialog.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { reducers } from './state';
import { ConversationsEffects } from './state/conversations';
import { ReviewersDetailsComponent } from './reviewers/reviewers-details/reviewers.details.component';
import { SampleStylesComponent } from './sample-styles/sample-styles.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CardGridComponent } from './shared/card-grid/card-grid.component';
import { MarkdownModule } from 'ngx-markdown';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { PromptBoxComponent } from './advisor_new/prompt-box/prompt-box.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatPseudoCheckboxModule } from '@angular/material/core';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { RowDetailsComponent } from './collections/row-details/row-details.component';
import { SelectedDocumentsMenuComponent } from './admin/manage-collections/selected-documents-menu/selected-documents-menu.component';
import { ProgressCellComponent } from './shared/progress-cell/progress-cell.component';
import { LibrariesComponent } from './Libraries/libraries.component';
import { LibrariesSelectedDocumentsMenuComponent } from './Libraries/libraries-selected-document-menu/libraries-selected-document-menu.component';
import { LottieComponent, provideLottieOptions } from 'ngx-lottie';
import { PingComponent } from './shared/lottie';
import { UserInformationComponent } from './shared/user-information/user-information.component';
import { PollingStatusIndicatorComponent } from './shared/polling-status-indicator/polling-status-indicator.component';
import { NoDataFoundComponent } from './shared/no-data-found/no-data-found.component';
import { CitationLinkComponent } from './advisor_new/message-list/citation-link/citation-link.component';
import { CitationGroupComponent } from './advisor_new/message-list/citation-group/citation-group.component';
import { MatPaginator } from '@angular/material/paginator';
import { LocatedTimePipe } from './pipes/located-time/located-time.pipe';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import {
  MatNestedTreeNode,
  MatTree,
  MatTreeNode,
  MatTreeNodeDef,
  MatTreeNodeOutlet,
  MatTreeNodePadding,
  MatTreeNodeToggle
} from '@angular/material/tree';
import { TreeTableComponent } from './Libraries/tree-table/tree-table.component';

// Amplify gets configured in configurationServiceFactory
// We make the below a promise so that we can load the configuration before the app starts
export function configurationServiceFactory(config: ConfigurationService) {
  return () =>
    config
      .loadConfiguration()
      .toPromise()
      .then(data => {
        if (data) config.setConfiguration(data);
      });
}

ModuleRegistry.registerModules([AllCommunityModule]);

function mockConditionalInterceptor(): Provider[] {
  if (environment.enableMocks) {
    return [
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MockInterceptor,
        deps: [ConfigurationService],
        multi: true
      }
    ];
  }
  return [];
}

@NgModule({
  declarations: [
    AppComponent,
    SourceDocumentDialogComponent,
    SourceDocumentDialogNewComponent,
    HomeComponent,
    PromptLogComponent,
    PlaygroundComponent,
    AdvisorComponent,
    AdvisorNewComponent,
    AlertModalComponent,
    ConfirmationModalComponent,
    DraftSectionDialogComponent,
    AdminComponent,
    UserModalComponent,
    ResearchDialogComponent,
    ViewResearchDialogComponent,
    EditCollectionDialogComponent,
    AddCollectionDialogComponent,
    ManageCollectionsComponent,
    ManageUsersComponent,
    ManagePersonasComponent,
    ManagePersonasViewComponent,
    ManagePersonasEditComponent,
    ManageSystemPromptsComponent,
    FilterModalComponent,
    UploadDialogComponent,
    ReviewersComponent,
    ReviewerInfoDialogComponent,
    ReviewersFilterDialogComponent,
    MultiSelectReviewDialogComponent,
    ReviewersDetailsComponent,
    SampleStylesComponent,
    RowDetailsComponent,
    ProgressCellComponent,
    LibrariesComponent,
    PollingStatusIndicatorComponent
  ],
  imports: [
    AmplifyAuthenticatorModule,
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatDialogModule,
    MatTabsModule,
    MatExpansionModule,
    AppRoutingModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatMenuModule,
    AgGridModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatSnackBarModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatGridListModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatPseudoCheckboxModule,
    MessageListComponent,
    MatDividerModule,
    MatSlideToggleModule,
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([ConversationsEffects]),
    CardGridComponent,
    MarkdownModule.forRoot(),
    ClipboardModule,
    PromptBoxComponent,
    SelectedDocumentsMenuComponent,
    LibrariesSelectedDocumentsMenuComponent,
    PingComponent,
    LottieComponent,
    UserInformationComponent,
    NoDataFoundComponent,
    CitationLinkComponent,
    CitationGroupComponent,
    MatPaginator,
    LocatedTimePipe,
    MatSortHeader,
    MatSort,
    CdkDropList,
    CdkDrag,
    MatTreeNodePadding,
    MatTreeNodeToggle,
    MatTreeNode,
    MatTree,
    MatNestedTreeNode,
    MatTreeNodeOutlet,
    MatTreeNodeDef,
    TreeTableComponent
  ],
  providers: [
    ConfigurationService,
    ...mockConditionalInterceptor(),
    provideLottieOptions({
      player: () => import('lottie-web')
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: configurationServiceFactory,
      deps: [ConfigurationService],
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi())
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
